import { Component, computed, effect, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';

// Components
import { AreaChartComponent } from '../charts/area-chart/area-chart.component';
import { DonutChartComponent } from '../charts/donut-chart/donut-chart.component';
import { StorageUsageComponent } from '../storage-usage/storage-usage.component';
import { NgbdDatepickerRangePopup } from '../datepicker-range-popup/datepicker-range-popup.component';
import { QueryCounterComponent } from "../query-counter/query-counter.component";
import { NavbarComponent } from "../../../template/components/navbar/navbar.component";

// Services
import { ReportService } from '../../services/report.service';
import { QuotaService } from '../../services/quota.service';

// Models
import { UtilizationResponse } from '../../models/utilization-response.model';

// FontAwesome
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faFileCsv, faFileExcel, faWarning } from '@fortawesome/free-solid-svg-icons';
import { NgxPermissionsService } from 'ngx-permissions';
import { toSignal } from '@angular/core/rxjs-interop';
import { finalize, from } from 'rxjs';


@Component({
  selector: 'app-utilization-dashboard',
  standalone: true,
  imports: [
    AreaChartComponent,
    DonutChartComponent,
    StorageUsageComponent,
    NgbdDatepickerRangePopup,
    QueryCounterComponent,
    NavbarComponent,
    FontAwesomeModule,
    DatePipe
  ],
  templateUrl: './utilization-dashboard.component.html',
  styleUrl: './utilization-dashboard.component.scss'
})
export class UtilizationDashboardComponent {
  private readonly reportSrv = inject(ReportService);
  private readonly permissionSrv = inject(NgxPermissionsService);
  quotaSrv = inject(QuotaService);

  navList = [{ label: 'Monitoraggio Utilizzo', routePath: '/utilization' }];

  contextListQuotas = this.quotaSrv.getQuotaDetails();

  contextListFilter = computed(() => [
    ...(this.contextListQuotas() ?? []).map(e => ({ label: e.name, value: e.id, selected: true }))
  ]);

  selectedContexts = signal<string[]>([]);

  licenseStartDate!: Date;
  lastUpdateDate!: Date;

  totalAvailableSpace = 0;
  totalAvailableRequests = 0;
  totalRequests = 0;

  dataSpace: { label: string; value: number, color: string }[] = [];
  dataQueryDonutChart: { label: string; value: number, color: string }[] = [];
  dataQueryAreaChart: { label: string; value: number }[] = [];
  dataSpaceAreaChart: { label: string; value: number }[] = [];

  readonly colors = [
    '#0033A1', '#520DA3', '#F3F5FA', '#E7EAF4', '#EB0B26',
    '#ffffff', '#000000', '#CFD7EB', '#B7C2E0', '#FF99CC', '#FFFF99',
    '#FFCCFF', '#CCFFCC', '#CCCCCC', '#999999', '#666666'
  ];

  selectedAreaChart = signal<'queries' | 'space'>('queries');

  hasAdmPermission = toSignal(
    from(this.permissionSrv.hasPermission('ROLE_ADM')),
    { initialValue: false }
  );

  dateFrom = signal<string>('');
  dateTo = signal<string>('');
  isCurrentInvoicePeriod = true;

  dataCache = signal<UtilizationResponse | null>(null);

  faFileCsv = faFileCsv;
  faFileExcel = faFileExcel;
  faWarning = faWarning;

  segmentDataLoading = signal<boolean>(false);

  constructor() {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();

    const prev25 = today.getDate() >= 25
      ? new Date(year, month, 25)          // 25 of the current month
      : new Date(year, month - 1, 25);     // 25 of the previous month

    this.dateFrom.set(this.formatDateToLocalISO(prev25));
    this.dateTo.set(this.formatDateToLocalISO(today));

    effect(() => {
      this.selectedContexts.set([...this.contextListFilter().map(e => e.value), 'cancelled']);
      this.getData(this.dateFrom(), this.dateTo());
    });
  }

  onDateRangeChange(dateRange: string) {
    console.debug('Selected date range:', dateRange);

    if (!dateRange.includes(' - ')) {
      console.error('Invalid date range format:', dateRange);
      return;
    }

    const [dateFromRaw, dateToRaw] = dateRange.split(' - ');

    this.checkCurrentInvoicePeriod(dateFromRaw, dateToRaw);

    if (dateFromRaw && dateToRaw) {
      this.dateFrom.set(dateFromRaw);
      this.dateTo.set(dateToRaw);
      this.getData(this.dateFrom(), this.dateTo());
    } else {
      console.error('Error parsing dates:', dateFromRaw, dateToRaw);
    }
  }

  onSelectedContextsChange(contextIdList: string[]) {
    this.selectedContexts.set(contextIdList);
    const cachedData = this.dataCache();
    if (cachedData) {
      this.filterData(cachedData, this.selectedContexts());
    }
  }

  get selectedChartData() {
    return this.selectedAreaChart() === 'queries'
      ? this.dataQueryAreaChart
      : this.dataSpaceAreaChart;
  }

  selectChart(chart: 'queries' | 'space') {
    this.selectedAreaChart.set(chart);
  }

  downloadReport(format: 'csv' | 'excel') {
    this.reportSrv.exportReport(this.dateFrom(), this.dateTo(), format).subscribe(response => {
      const contentType = response.headers.get('Content-Type') || (format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv');
      const blob = new Blob([response.body!], { type: contentType });
      const fileName = this.getFileNameFromContentDisposition(response.headers.get('Content-Disposition'), format);

      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
    });
  }

  private getFileNameFromContentDisposition(contentDisposition: string | null, format: 'csv' | 'excel'): string {
    const match = /filename="([^"]+)"/.exec(contentDisposition ?? '');
    if (match) {
      return match[1];
    }

    // fallback se Content-Disposition non è presente o il filename è malformato
    return `report_utilizzo-${this.dateFrom()}_${this.dateTo()}.${format === 'excel' ? 'xlsx' : 'csv'}`;
  }

  // Input date in formato yyyy-MM-dd
  private checkCurrentInvoicePeriod(dateFromRaw: string | null, dateToRaw: string | null) {
    const today = new Date();

    // Calcola il 25 del mese precedente in local time
    const prevMonth25 = new Date(today.getFullYear(), today.getMonth() - 1, 25);

    const formattedPrevMonth25 = this.formatDateToLocalISO(prevMonth25);
    const formattedToday = this.formatDateToLocalISO(today);

    const isValidFrom = dateFromRaw === formattedPrevMonth25;
    const isValidTo = dateToRaw === formattedToday;

    this.isCurrentInvoicePeriod = (isValidFrom && isValidTo);
  }

  // Formatta una data come yyyy-MM-dd in fuso orario locale
  private formatDateToLocalISO(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getData(fromDate: string, toDate: string): void {
    this.segmentDataLoading.set(true);
    this.reportSrv.getUtilizationData(fromDate, toDate)
      .pipe(finalize(() => this.segmentDataLoading.set(false)))
      .subscribe({
        next: (data) => {
          this.dataCache.set(data);
          this.licenseStartDate = new Date(data.licenseInfo.startDate);
          this.lastUpdateDate = new Date(data.licenseInfo.updatedAt);
          this.filterData(data, this.selectedContexts());
        }
      });
  }

  private filterData(data: UtilizationResponse, selectedContextIds?: string[]) {
    // Filtra i contesti se è presente una lista di ID selezionati
    const filteredSegments = data.segments.map(segment => ({
      ...segment,
      contexts: selectedContextIds
        ? segment.contexts.filter(ctx => selectedContextIds.includes(ctx.contextId))
        : segment.contexts
    }));

    // Ricalcola i totali basati sui segmenti filtrati
    this.totalAvailableSpace = data.licenseInfo.storage;
    this.totalAvailableRequests = data.licenseInfo.provisionedRequests || data.licenseInfo.requests;

    this.totalRequests = filteredSegments.reduce(
      (sum, segment) => sum + segment.contexts.reduce((ctxSum, ctx) => ctxSum + ctx.requests, 0),
      0
    );

    // Aggregazioni e dati per i grafici basati sui segmenti filtrati
    const filteredData: UtilizationResponse = {
      ...data,
      segments: filteredSegments
    };

    this.dataSpace = this.buildDataSpace();

    this.dataQueryDonutChart = this.aggregateContexts(filteredData, 10, 'requests');

    this.dataQueryAreaChart = filteredSegments
      .map(segment => ({
        label: segment.date,
        value: segment.contexts.reduce((sum, ctx) => sum + ctx.requests, 0)
      }))
      .sort((a, b) => new Date(a.label).getTime() - new Date(b.label).getTime());

    this.dataSpaceAreaChart = filteredSegments
      .map(segment => ({
        label: segment.date,
        value: segment.contexts.reduce((sum, ctx) => sum + ctx.storage, 0) / (1024 * 1024) // Convert bytes to MB
      }))
      .sort((a, b) => new Date(a.label).getTime() - new Date(b.label).getTime());
  }

  private buildDataSpace() {
    const quotas = this.contextListQuotas() ?? [];
    const selectedIds = new Set(this.selectedContexts());

    const getColor = (id: string, idx: number) =>
      (id === 'others' || id === 'cancelled')
        ? 'rgba(220, 220, 220)'
        : this.colors[idx % this.colors.length];

    return quotas.reduce<{ label: string; value: number; color: string }[]>((acc, q, idx) => {
      if (!selectedIds.has(q.id)) return acc;

      acc.push({
        label: q.name,
        value: q.totalSpaceOccupied,
        color: getColor(q.id, idx)
      });

      return acc;
    }, []);
  }

  private aggregateContexts(data: UtilizationResponse, limit: number, key: 'requests' | 'storage'): { label: string; value: number; color: string }[] {
    const contextMap = new Map<string, { id: string; value: number }>();

    for (const segment of data.segments) {
      for (const context of segment.contexts) {
        const value = context[key] || 0;
        const prev = contextMap.get(context.contextName);
        contextMap.set(context.contextName, {
          id: context.contextId,
          value: (prev?.value || 0) + value
        });
      }
    }

    let result = Array.from(contextMap.entries())
      .map(([label, { id, value }]) => ({ label, value, id }))
      .sort((a, b) => b.value - a.value);

    // Trova e sposta 'others' e 'cancelled' in coda
    const specialIds = ['others', 'cancelled'];
    let specialItems: any[] = [];
    result = result.filter(item => {
      if (specialIds.includes(item.id)) {
        specialItems.push(item);
        return false;
      }
      return true;
    });
    result = [...result, ...specialItems];

    // Aggiungi il campo color tramite index, ma per 'others' o 'cancelled' usa sempre il colore specificato
    return result.slice(0, limit).map(({ label, value, id }, idx) => ({
      label,
      value,
      color: (id === 'others' || id === 'cancelled') ? 'rgba(220, 220, 220)' : this.colors[idx % this.colors.length]
    }));
  }
}
